"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Users, TrendingUp, Clock, Building2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { FAQSection } from "@/components/landing/faq-section";
import { useLandingCta } from "@/hooks/use-landing-cta";
import { SOCIAL_PROOF } from "@/data/social-proof";
// Note: This page doesn't have LandingPricingSection - it's B2B focused

const DEALER_SHOWCASE = [
  {
    name: "Showroom Example",
    before: "/showcase/images/before5.webp",
    imageClassName: "object-[center_bottom]",
    wheels: [
      {
        id: "dealer-1",
        name: "Option A",
        image: "/showcase/images/after51.webp",
        thumbnail: "/showcase/images/after51.webp",
      },
      {
        id: "dealer-2",
        name: "Option B",
        image: "/showcase/images/after52.webp",
        thumbnail: "/showcase/images/after52.webp",
      },
      {
        id: "dealer-3",
        name: "Option C",
        image: "/showcase/images/after53.webp",
        thumbnail: "/showcase/images/after53.webp",
      },
    ],
  },
];

const DEALER_BENEFITS = [
  {
    icon: <TrendingUp className="h-5 w-5" />,
    title: "Close deals faster",
    description: "Customers who see the result are more confident buyers. Visual proof beats catalog descriptions.",
  },
  {
    icon: <Clock className="h-5 w-5" />,
    title: "Save time on test-fits",
    description: "No need to mount wheels for a preview. Show options in seconds instead of hours.",
  },
  {
    icon: <Users className="h-5 w-5" />,
    title: "Reduce returns",
    description: "When customers see exactly what they're getting, buyer's remorse drops significantly.",
  },
];

const DEALER_WORKFLOW = [
  {
    step: "01",
    title: "Customer arrives with their car",
    description: "Take a quick photo of their car in your lot or showroom. Any smartphone works.",
  },
  {
    step: "02",
    title: "Show wheel options",
    description: "Upload the photo and show them different wheels from your inventory. Switch styles instantly.",
  },
  {
    step: "03",
    title: "Customer decides with confidence",
    description: "They see exactly how the wheels will look. No imagination required-just clear visuals.",
  },
];

const DEALER_FAQS = [
  {
    question: "How do we use this in the showroom?",
    answer:
      "Simple: snap a photo of your customer's car, upload it to the tool, and show them wheel options on their actual car. Takes under a minute to generate each preview.",
  },
  {
    question: "Can we use our own wheel inventory images?",
    answer:
      "Yes. Upload product photos from your inventory, supplier catalogs, or manufacturer websites. The tool works with any wheel image.",
  },
  {
    question: "Is there volume pricing for dealers?",
    answer:
      "We offer credit packages that work well for high-volume shops. Contact us for details on dealer accounts with shared team credits.",
  },
  {
    question: "Can customers access their previews after leaving?",
    answer:
      "Yes. Download previews as HD images and send them to customers via email or text. They can review options at home and come back ready to buy.",
  },
  {
    question: "What about branded previews?",
    answer:
      "Currently, previews are unbranded-clean images you can use however you like. Contact us if you need custom branding options.",
  },
];

export function ForDealersContent() {
  const { showStickyCta, ctaText, handleTryItNow } = useLandingCta();

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Wheel Visualizer for Dealers",
    url: "https://thewheelvisualizer.com/for-dealers",
    description:
      "Help your customers see wheels before they buy. Wheel visualization tool for dealerships and tire shops.",
    mainEntity: {
      "@type": "SoftwareApplication",
      name: "The Wheel Visualizer",
      operatingSystem: "Web",
      applicationCategory: "BusinessApplication",
      audience: {
        "@type": "Audience",
        audienceType: "Automotive dealers, tire shops, wheel retailers",
      },
    },
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      {/* Hero - B2B focused */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
                <Building2 className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/70">For dealers & shops</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Help customers see wheels before they buy
              </h1>
              
              <p className="text-lg text-white/60 mb-8 leading-relaxed">
                Show your customers exactly how new wheels will look on their car. 
                Close more deals, reduce returns, and stand out from competitors still using catalog photos.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={handleTryItNow}
                  className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
                >
                  Try It Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-white/20 text-white hover:bg-white/5 h-12 px-8"
                >
                  <Link href="/contact?intent=dealer">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Contact Sales
                  </Link>
                </Button>
              </div>

              <p className="text-white/40 text-sm mt-6">
                No subscription required. Pay only for what you use.
              </p>
            </div>

            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <Image
                  src="/showcase/images/after52.webp"
                  alt="Wheel preview for dealerships"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
              </div>
              {/* Floating stat */}
              <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl bg-[#0A0C0F] border border-white/10">
                <div className="text-2xl font-bold text-white">{SOCIAL_PROOF.users.value}</div>
                <div className="text-xs text-white/50">Dealers & shops on the platform</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Why dealers use it</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Turn browsers into buyers
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {DEALER_BENEFITS.map((benefit) => (
              <div key={benefit.title} className="group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 text-white/70 group-hover:text-white group-hover:border-white/20 transition-colors">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                <p className="text-white/60 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How dealers use it */}
      <section className="py-24 px-6 bg-[#05070C]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Workflow</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              How it works in your shop
            </h2>
          </div>

          <div className="space-y-8">
            {DEALER_WORKFLOW.map((item, idx) => (
              <div
                key={item.step}
                className="relative flex gap-8 items-start"
              >
                {/* Connector line */}
                {idx < DEALER_WORKFLOW.length - 1 && (
                  <div className="absolute left-6 top-16 w-px h-16 bg-gradient-to-b from-white/20 to-transparent" />
                )}
                
                <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-semibold text-white/70">{item.step}</span>
                </div>
                
                <div className="pt-2">
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-white/60 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dealer pricing */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Dealer credits</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-white">
                Bulk credits for showroom use
              </h2>
              <p className="text-white/60 mt-5 leading-relaxed">
                Use previews during the sales conversation without committing to a subscription.
                Start with a small pack, then move to shared team credits when the visualizer
                becomes part of your showroom workflow.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <ul className="space-y-4 text-white/70">
                <li className="flex gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-white/50 shrink-0" />
                  Shared credits for shops with multiple staff
                </li>
                <li className="flex gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-white/50 shrink-0" />
                  No monthly commitment for occasional showroom use
                </li>
                <li className="flex gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-white/50 shrink-0" />
                  Contact us for higher-volume dealer packs
                </li>
              </ul>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="mt-8 w-full border-white/20 text-white hover:bg-white/5"
              >
                <Link href="/contact?intent=dealer">Ask About Dealer Credits</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Live demo</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Show this to your customers
            </h2>
            <p className="text-white/60 mt-4">
              Drag to compare. Tap thumbnails to switch options. This is what they&apos;ll see.
            </p>
          </div>

          {DEALER_SHOWCASE.map((scenario) => (
            <InteractiveShowcase
              key={scenario.name}
              beforeImage={scenario.before}
              wheels={scenario.wheels}
              imageClassName={scenario.imageClassName}
              priority
            />
          ))}
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 px-6 bg-[#05070C]">
        <div className="max-w-4xl mx-auto">
          <div className="relative p-8 md:p-12 rounded-2xl bg-white/[0.02] border border-white/5">
            <div className="text-5xl text-white/10 font-serif absolute top-6 left-8">&ldquo;</div>
            <blockquote className="relative z-10">
              <p className="text-xl md:text-2xl text-white leading-relaxed mb-8">
                We use it with customers in the showroom. It closes deals faster because they can finally see the result.
              </p>
              <footer className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden border border-white/10">
                  <Image
                    src="/testimonials/paul-a.webp"
                    alt="Paul Anderson"
                    width={48}
                    height={48}
                    className="object-cover"
                  />
                </div>
                <div>
                  <div className="font-semibold text-white">Paul Anderson</div>
                  <div className="text-sm text-white/50">Wheel Dealer</div>
                </div>
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#04060A]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Questions from dealers
            </h2>
            <p className="text-white/60 mt-4">
              What shops and dealerships ask us most.
            </p>
          </div>
          <FAQSection faqs={DEALER_FAQS} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to close more wheel sales?
          </h2>
          <p className="text-white/60 mb-8">
            Start showing customers what their car will look like with new wheels. No commitment, no subscription.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={handleTryItNow}
              className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
            >
              Try It Free
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-white/20 text-white hover:bg-white/5 h-12 px-8"
            >
              <Link href="/contact?intent=dealer">
                Contact for Volume Pricing
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Sticky CTA */}
      {showStickyCta && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#05070C]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3">
            <button
              onClick={handleTryItNow}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white text-black font-semibold"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
